import { Controller } from 'react-hook-form';
import ReactSelect, { StylesConfig } from 'react-select';

export type SearchOption = { value: string; label: string };

type ISearchSelect = {
	/** A react-hook-form control. Without one the select is driven by `value` and `onChange`. */
	control?: any;
	name: string;
	value?: string;
	onChange?: (value: string) => void;
	isClearable?: boolean;
	isDisabled?: boolean;
	label?: string;
	options: SearchOption[];
	placeholder?: string;
	isRequired?: boolean;
	requiredMessage?: string;
	isError?: boolean;
	errorMessage?: string;
	noMargin?: boolean;
};

/**
 * Bootstrap's form-select-sm look, in theme tokens so it follows the company colour and dark mode.
 * The menu is portalled to <body>: inside a card it would otherwise be clipped.
 */
const styles = (isError?: boolean): StylesConfig<SearchOption, false> => ({
	control: (base, state) => ({
		...base,
		minHeight: 31,
		fontSize: '0.875rem',
		backgroundColor: 'var(--bs-body-bg)',
		borderColor: isError ? 'var(--bs-form-invalid-border-color)' : state.isFocused ? 'var(--bs-primary)' : 'var(--bs-border-color)',
		borderRadius: 'var(--bs-border-radius-sm)',
		boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25)' : 'none',
		'&:hover': { borderColor: state.isFocused ? 'var(--bs-primary)' : 'var(--bs-border-color)' },
	}),
	valueContainer: (base) => ({ ...base, padding: '0 0.5rem' }),
	singleValue: (base) => ({ ...base, color: 'var(--bs-body-color)' }),
	input: (base) => ({ ...base, color: 'var(--bs-body-color)', margin: 0, padding: 0 }),
	placeholder: (base) => ({ ...base, color: 'var(--bs-secondary-color)' }),
	indicatorSeparator: () => ({ display: 'none' }),
	dropdownIndicator: (base) => ({ ...base, padding: '0 0.5rem', color: 'var(--bs-body-color)' }),
	menuPortal: (base) => ({ ...base, zIndex: 1060 }),
	menu: (base) => ({
		...base,
		fontSize: '0.875rem',
		backgroundColor: 'var(--bs-body-bg)',
		border: '1px solid var(--bs-border-color)',
		boxShadow: 'var(--bs-box-shadow)',
	}),
	option: (base, state) => ({
		...base,
		color: state.isSelected ? '#fff' : 'var(--bs-body-color)',
		backgroundColor: state.isSelected
			? 'var(--bs-primary)'
			: state.isFocused
				? 'var(--bs-tertiary-bg)'
				: 'transparent',
	}),
	noOptionsMessage: (base) => ({ ...base, color: 'var(--bs-secondary-color)' }),
});

/**
 * A searchable single select over a STRING value: it stores option.value, never the option
 * object, so a form submits what a plain <Select> would. For long lists where a native select
 * only jumps by first letters (time zones, a table list). Bound to a react-hook-form field when
 * given `control`, otherwise to `value`/`onChange` (a filter card).
 */
const SearchSelect = ({
	control,
	name,
	value,
	onChange,
	isClearable,
	isDisabled,
	label,
	options,
	placeholder,
	isRequired,
	requiredMessage,
	isError,
	errorMessage,
	noMargin,
}: ISearchSelect) => {
	// A render helper, not a component: a component declared in here would be a new type on every
	// render, and React would remount the select (closing its menu) each time.
	const renderSelect = (current: string, change: (v: string) => void, onBlur?: () => void) => (
		<ReactSelect<SearchOption, false>
			inputId={name}
			options={options}
			placeholder={placeholder}
			value={options.find((o) => o.value === current) || null}
			onChange={(o) => change(o ? o.value : '')}
			onBlur={onBlur}
			isClearable={isClearable}
			isDisabled={isDisabled}
			menuPortalTarget={document.body}
			styles={styles(isError)}
			noOptionsMessage={() => 'Nothing matches'}
		/>
	);

	return (
	<div className={noMargin ? '' : 'mb-3'}>
		{label && (
			<label className='form-label d-flex align-items-center gap-1' htmlFor={name}>
				<span className={isRequired ? 'required' : ''}>{label}</span>
			</label>
		)}
		{control ? (
			<Controller
				control={control}
				name={name}
				rules={isRequired ? { required: requiredMessage || true } : undefined}
				render={({ field }) => renderSelect(field.value, field.onChange, field.onBlur)}
			/>
		) : (
			renderSelect(value || '', (v) => onChange?.(v))
		)}
		{isError && <div className='invalid-feedback d-block'>{errorMessage}</div>}
	</div>
	);
};

export { SearchSelect };
